import React, { FC, useEffect, useState } from 'react';
import CommonButton from 'components/CommonButton/CommonButton';
import Drawer from 'components/Drawer/Drawer';
import { IService } from 'interfaces/services';
import ExpandWrapper from 'components/ExpandWrapper/ExpandWrapper';
import { Controller, FormProvider, useForm } from 'react-hook-form';

import useServiceSchedule from 'hooks/useServiceSchedule';
import LoadingSpinner from 'components/LoadingSpinner/LoadingSpinner';
import { ServiceSchedule } from 'interfaces/serviceSchedule';
import RegularWorkingHour from './RegularWorkingHour/RegularWorkingHour';
import axiosInstance from 'apis/axiosInstance';
import { renderToast } from 'components/Toast/Toast';
import { Option } from 'pages/PractitionerPage/MainSection/PractitionerProfile/MultipleSelect/MultipleSelect';
import BlockoutType from './BlockoutType/BlockoutType';
import { isEqual } from 'lodash';
import useMatchMutate from 'hooks/useMatchMutate';
import CopySettingSection from './CopySettingSection/CopySettingSection';
import ServiceCustomDaysList from './ServiceCustomDayList/ServiceCustomDaysList';

interface AdvancedSettingProps {
  service: IService;
  isDrawerOpen: boolean;
  onClose: () => void;
  services: IService[];
  onSuccessfullyEdited: () => void;
}

export interface TimeSlots {
  start: string | null;
  end: string | null;
  operatories: Option[];
}

export interface TimeFrame {
  day: string;
  timeSlots: TimeSlots[];
}

export interface TimeFrameFormValues {
  serviceName: string;
  duration: number;
  operatories: string[];
  timeFrames: TimeFrame[];
  syncedWorkingHourLabelId: string | null;
}

const AdvancedSetting: FC<AdvancedSettingProps> = ({
  service,
  isDrawerOpen,
  onClose,
  onSuccessfullyEdited,
  services,
}) => {
  const methods = useForm<TimeFrameFormValues>({
    mode: 'all',
    defaultValues: {
      serviceName: service.name,
      duration: service.duration,
      syncedWorkingHourLabelId: service.syncedWorkingHourLabel
        ? service.syncedWorkingHourLabel.id
        : null,
      operatories: [],
      timeFrames: [],
    },
  });

  const { control, reset, watch, handleSubmit, formState } = methods;

  const [isCustomDaysTab, setIsCustomDaysTab] = useState(false);

  const matchMutate = useMatchMutate();

  const { data: serviceSchedule, isLoading: isServiceScheduleLoading } =
    useServiceSchedule(service.id);

  useEffect(() => {
    if (!serviceSchedule) return;

    const normalizedData = Object.entries(serviceSchedule).map(
      (dayServiceSchedule) => {
        const [day, value] = dayServiceSchedule;
        const serviceSchedule = value as ServiceSchedule[];

        const timeSlots = serviceSchedule.map((timeSlot) => ({
          start: timeSlot.startTime,
          end: timeSlot.endTime,
          operatories: timeSlot.operatories.map((operatory) => ({
            label: operatory.name,
            value: operatory.id,
          })),
        }));

        return {
          day,
          timeSlots,
        };
      }
    );

    const sortedNormalizedData = normalizedData.sort((a, b) => {
      const dayOrder = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
      return dayOrder.indexOf(a.day) - dayOrder.indexOf(b.day);
    });

    reset({
      timeFrames: sortedNormalizedData,
      serviceName: service.name,
      duration: service.duration,
      syncedWorkingHourLabelId: service.syncedWorkingHourLabel
        ? service.syncedWorkingHourLabel.id
        : null,
      operatories: [],
    });
  }, [
    serviceSchedule,
    reset,
    service.duration,
    service.name,
    service.syncedWorkingHourLabel,
  ]);

  const timeFrames = watch('timeFrames');

  const syncedWorkingHourLabelId = watch('syncedWorkingHourLabelId');

  const saveServiceSchedule = async (data: TimeFrameFormValues) => {
    const payload = data.timeFrames.map((timeFrame) => {
      const { day, timeSlots } = timeFrame;
      const isAvailable = timeSlots.some(
        (timeSlot) => timeSlot.start && timeSlot.end
      );
      const shifts = timeSlots.map((timeSlot) => ({
        startTime: timeSlot.start,
        endTime: timeSlot.end,
        operatoryIds: timeSlot.operatories.map((operatory) => operatory.value),
      }));

      return {
        day,
        isAvailable,
        shifts: isAvailable
          ? shifts
          : [{ startTime: null, endTime: null, operatoryIds: [] }],
      };
    });

    await axiosInstance.post(`/services/${service.id}/schedules`, payload);
  };

  const saveServiceSetting = async (data: TimeFrameFormValues) => {
    let syncedWorkingHourLabelId = undefined;

    if (formState.dirtyFields.syncedWorkingHourLabelId) {
      syncedWorkingHourLabelId = data.syncedWorkingHourLabelId
        ? data.syncedWorkingHourLabelId
        : null;
    }

    if (syncedWorkingHourLabelId === undefined) {
      return;
    }

    await axiosInstance.patch(`block-scheduling`, [
      { serviceId: service.id, syncedWorkingHourLabelId },
    ]);

    onSuccessfullyEdited();
  };

  const onSave = async (data: TimeFrameFormValues) => {
    try {
      await saveServiceSchedule(data);
      await saveServiceSetting(data);

      matchMutate(/\/exceptions[\s\S]+/);

      renderToast({
        type: 'success',
        message: 'Service settings saved successfully',
      });
      onClose();
    } catch (error) {
      renderToast({
        type: 'error',
        message: 'Failed to save service settings. Please try again.',
      });
    }
  };

  const isDataChanged =
    formState.dirtyFields.syncedWorkingHourLabelId ||
    timeFrames.some(
      (timeFrame, index) =>
        !isEqual(timeFrame, formState.defaultValues?.timeFrames?.[index])
    );

  const isDataValid = timeFrames.every((timeFrame) =>
    timeFrame.timeSlots.every(
      (timeSlot) =>
        (!timeSlot.start && !timeSlot.end) ||
        (timeSlot.start && timeSlot.end && timeSlot.operatories.length > 0)
    )
  );

  return (
    <Drawer
      title={`${service.name} settings`}
      isShowing={isDrawerOpen}
      setIsShowing={onClose}
      footer={
        <div className="w-full h-6 flex px-2.4 justify-end items-center shadow-primary sticky bottom-0 bg-white">
          <CommonButton
            className="!min-w-[12rem]"
            onClick={handleSubmit(onSave)}
            isLoading={formState.isSubmitting}
            disabled={!isDataChanged || !isDataValid}
          >
            Save
          </CommonButton>
        </div>
      }
    >
      <div className="mb-2">
        <CopySettingSection
          currentService={service}
          services={services}
          timeFrames={timeFrames}
          isDataValid={isDataValid}
          syncedWorkingHourLabelId={syncedWorkingHourLabelId}
        />
      </div>
      <div className="flex flex-col gap-y-0.8">
        <Controller
          name={'syncedWorkingHourLabelId'}
          control={control}
          render={({ field }) => {
            return <BlockoutType field={field} />;
          }}
        />
      </div>

      <ExpandWrapper
        className="first-child:w-full"
        header={
          <span className="text-16 leading-[2.4rem] font-bold">
            Time Frames
          </span>
        }
      >
        <div className="-mx-3 pt-1 -mb-2.1">
          <div className="w-full">
            <div className="h-4.8 flex justify-evenly items-center border border-light-grey">
              <button
                className={`basis-[13rem] cursor-pointer ${
                  !isCustomDaysTab ? 'text-magenta font-bold' : ''
                }`}
                onClick={() => setIsCustomDaysTab(false)}
              >
                Regular Hours
              </button>
              <span className="h-3 border-l border-light-grey"></span>
              <button
                className={`basis-[13rem] cursor-pointer ${
                  isCustomDaysTab ? 'text-magenta font-bold' : ''
                }`}
                onClick={() => setIsCustomDaysTab(true)}
              >
                Custom Days
              </button>
            </div>
          </div>
          {isCustomDaysTab ? (
            <ServiceCustomDaysList service={service} />
          ) : (
            <FormProvider {...methods}>
              {isServiceScheduleLoading ? (
                <div className="mt-3 flex justify-center items-center">
                  <LoadingSpinner className="all-child:fill-magenta" />
                </div>
              ) : (
                timeFrames.map((timeFrame) => (
                  <RegularWorkingHour
                    key={timeFrame.day}
                    timeFrame={timeFrame}
                  />
                ))
              )}
            </FormProvider>
          )}
        </div>
      </ExpandWrapper>
    </Drawer>
  );
};

export default AdvancedSetting;
